import { inject, Injectable } from '@angular/core';
import { angularjs } from '@app/service/migration-helpers/angularjs.injection';
import type { StatsdService } from '@app/interface/common.interface';
import type { AnalyticsService } from '@app/interface/service/analytics-service.interface';
import type { ILoggerService } from '@app/service/logger.service';

@Injectable()
export class ApplePayTelemetryService {
  static readonly BRAINTREE_CLIENT_INIT_FAILURE_COUNTER = 'BRAINTREE_CLIENT_INITIALIZATION_FAILURE';
  static readonly APPLE_PAY_INIT_FAILURE_COUNTER = 'APPLE_PAY_INITIALIZATION_FAILURE';

  private readonly analyticsService: AnalyticsService = inject(angularjs.AnalyticsService);
  private readonly loggerService: ILoggerService = inject(angularjs.loggerService);
  private readonly statsdService: StatsdService = inject(angularjs.StatsdService);

  reportApplePayStart(): void {
    this.analyticsService.recordEvent(this.analyticsService.events.APPLEPAY_START);
  }

  reportBraintreeInitFailure(message: string): void {
    this.loggerService.error(message, { cause: 'Braintree client initialization error' });
    this.statsdService.incrementCounter(ApplePayTelemetryService.BRAINTREE_CLIENT_INIT_FAILURE_COUNTER);
  }

  reportApplePayInitFailure(message: string): void {
    this.analyticsService.recordEvent(this.analyticsService.events.APPLEPAY_ERROR);
    this.loggerService.error(message, { cause: 'ApplePay initialization error' });
    this.statsdService.incrementCounter(ApplePayTelemetryService.APPLE_PAY_INIT_FAILURE_COUNTER);
  }

  reportApplePayCancel(): void {
    this.analyticsService.recordEvent(this.analyticsService.events.APPLEPAY_CANCEL);
  }

  reportRepriceTaxError(err: any): void {
    this.loggerService.error(err, { cause: 'Apple Pay reprice/tax error' });
  }

  reportPaymentCompletionError(err: any): void {
    this.loggerService.error(err, { cause: 'Apple Pay payment completion error' });
  }

  reportMissingBillingAddress(): void {
    this.analyticsService.recordEvent(this.analyticsService.events.APPLEPAY_ERROR);
    this.loggerService.error('Apple Pay returned no billing address for a billing-only cart', {
      cause: 'Apple Pay missing billing address'
    });
  }

  reportInvalidEmail(): void {
    this.analyticsService.recordEvent(this.analyticsService.events.APPLEPAY_ERROR);
    this.loggerService.error('Apple Pay returned an invalid email address', {
      cause: 'Apple Pay invalid email'
    });
  }

  reportEmailValidationServiceError(err: any): void {
    this.loggerService.error(err, { cause: 'Apple Pay email validation service error' });
  }

  reportShippingRepricingError(err: any): void {
    this.loggerService.error(err, { cause: 'Apple Pay shipping repricing error' });
  }

  reportOrderSubmissionFailure(message: string): void {
    this.loggerService.error(message, { cause: 'Apple Pay order submission failed' });
  }

  reportOrderSubmissionError(err: any): void {
    this.loggerService.error(err, { cause: 'Apple Pay order submission error' });
  }

  reportBraintreeFailedToValidateMerchant(err: any): void {
    this.loggerService.error(err, { cause: 'failed to validate merchant' });
  }

  reportFailedToTokenize(err: any): void {
    this.loggerService.error(err, { cause: 'Error tokenizing Apple Pay' });
  }
}
